import { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LockScreen({ usuario, onUnlock, onLogout }) {
  const [senha, setSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!senha.trim()) {
      setErro("Digite sua senha");
      return;
    }

    setLoading(true);
    setErro("");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/auth/unlock`,
        null,
        { params: { token, senha } }
      );

      if (response.data.success) {
        onUnlock();
      } else {
        if (response.data.force_logout) {
          onLogout();
        } else {
          setErro(response.data.message || "Senha incorreta");
        }
      }
    } catch (error) {
      console.error("Erro ao desbloquear:", error);
      setErro("Erro ao verificar senha. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("usuario");
    localStorage.removeItem("empresa");
    onLogout();
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      {/* Background blur overlay */}
      <div className="absolute inset-0 backdrop-blur-md bg-black/50"></div>
      
      <Card className="relative z-10 w-full max-w-md mx-4 shadow-2xl border-slate-700 bg-slate-800/90">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-amber-500" />
          </div>
          <CardTitle className="text-xl text-white">Sessão Bloqueada</CardTitle>
          <p className="text-sm text-slate-400 mt-2">
            Sua sessão expirou por inatividade
          </p>
        </CardHeader>
        
        <CardContent>
          {/* Avatar e Nome do Usuário */}
          <div className="flex items-center gap-3 mb-6 p-3 bg-slate-700/50 rounded-lg">
            <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg">
              {usuario?.nome?.charAt(0) || "U"}
            </div>
            <div>
              <p className="font-medium text-white">{usuario?.nome || "Usuário"}</p>
              <p className="text-sm text-slate-400">{usuario?.email || ""}</p>
            </div>
          </div>

          <form onSubmit={handleUnlock} className="space-y-4">
            <div className="relative">
              <Input
                type={mostrarSenha ? "text" : "password"}
                placeholder="Digite sua senha para desbloquear"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                className="pr-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                autoFocus
                data-testid="unlock-password-input"
              />
              <button
                type="button"
                onClick={() => setMostrarSenha(!mostrarSenha)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {mostrarSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {erro && (
              <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{erro}</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading}
              data-testid="unlock-button"
            >
              {loading ? "Verificando..." : "Desbloquear"}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full text-slate-400 hover:text-white hover:bg-slate-700"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              Sair e fazer login novamente
            </Button>
          </form>

          <p className="text-xs text-slate-500 text-center mt-4">
            Por segurança, a tela foi bloqueada após 10 minutos de inatividade
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
